import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts-pro";
import {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import React, { useEffect, useState } from "react";
import { useError } from "../../contexts/ErrorContext";
import useApi from "../../hooks/useGaneshApi";
import { SampleSummaryRecord_T } from "../../types/Sample";
import { formatNumber } from "utils/formatting";
import GaneshDataGrid from "../DataGrid/GaneshDataGrid";

type Granularity_T = {
  label: string;
  value: string;
};

type ChartDataRecord = {
  x: string;
  y: number | null;
};

type SampleMetricsKeys =
  | "count_customers"
  | "count_samples"
  | "mean_sales_to_customer"
  | "mean_ops_to_customer";

const granularityOptions: Granularity_T[] = [
  { label: "Weekly", value: "Week" },
  { label: "Monthly", value: "Month" },
  { label: "Quarterly", value: "Quarter" },
  { label: "Yearly", value: "Year" },
];

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "id", sort: "desc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
};

const transformYoYData = (
  rawData: SampleSummaryRecord_T[],
  dateKey: keyof SampleSummaryRecord_T,
  dataKey: SampleMetricsKeys
) => {
  const uniqueYears = Array.from(
    new Set(rawData.map((item) => item[dateKey]?.toString().slice(-4)))
  ).filter((year): year is string => !!year);

  const series = uniqueYears.map((year) => ({
    dataKey: year,
    label: year,
    formatNumber,
  }));

  const transformedData: Record<string, Record<string, number | string>> = {};

  rawData.forEach((item) => {
    const period = item[dateKey]?.toString().slice(0, -5).trim() || "";
    const year = item[dateKey]?.toString().slice(-4) || "";

    if (!transformedData[period]) {
      transformedData[period] = { [dateKey]: period };
    }
    transformedData[period][year] = (item[dataKey] ?? 0) as number | string;
  });

  const resultArray = Object.values(transformedData);
  resultArray.forEach((item) =>
    uniqueYears.forEach((yr) => {
      item[yr] = item[yr] ?? 0;
    })
  );

  return { data: resultArray, series };
};

const CompletedHistoricallySamples: React.FC<any> = () => {
  const [granularity, setGranularity] = useState<Granularity_T>(
    granularityOptions[2]
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [sampleSummaryRecords, setSampleSummaryRecords] = useState<
    SampleSummaryRecord_T[]
  >([]);
  const [chartView, setChartView] = useState<string | null>("customers");
  const [yoyChecked, setYoYToggle] = useState<boolean>(false);

  const [chartData, setChartData] = useState<Record<string, ChartDataRecord[]>>(
    {}
  );
  const [yoyChartData, setYoyChartData] = useState<Record<string, any>>({});
  const [seriesData, setSeriesData] = useState<any[]>([]);

  const { showError } = useError();
  const { apiRequest } = useApi();

  const fetchCompletedSampleStats = async () => {
    try {
      setLoading(true); // Set loading to true before the API request
      const response = await apiRequest<SampleSummaryRecord_T[]>(
        `/sample_metrics/completed_stats/${granularity.label}`,
        { method: "GET" }
      );
      if (response.data) setSampleSummaryRecords(response.data);
    } catch (error) {
      console.error(error);
      showError(`Error fetching completed sample stats.`);
    } finally {
      setLoading(false); // Set loading to false after data is fetched
    }
  };

  const prepareChartData = (
    records: SampleSummaryRecord_T[],
    keys: SampleMetricsKeys[]
  ) => {
    const chartData: Record<string, ChartDataRecord[]> = {};
    const yoyChartData: Record<string, any> = {};

    keys.forEach((key) => {
      chartData[key] = records.map((item) => ({
        x: item.completed_date_period || "",
        y: item[key] ?? null,
      }));
      yoyChartData[key] = transformYoYData(
        records,
        "completed_date_period",
        key
      );
    });

    return { chartData, yoyChartData };
  };

  useEffect(() => {
    fetchCompletedSampleStats();
  }, [granularity]);

  useEffect(() => {
    const keys: SampleMetricsKeys[] = [
      "count_customers",
      "count_samples",
      "mean_sales_to_customer",
      "mean_ops_to_customer",
    ];
    if (sampleSummaryRecords.length > 0) {
      const { chartData, yoyChartData } = prepareChartData(
        sampleSummaryRecords,
        keys
      );
      setChartData(chartData);
      setYoyChartData(yoyChartData);
      setSeriesData(yoyChartData["count_customers"].series);
    } else {
      setChartData({});
      setYoyChartData({});
    }
  }, [sampleSummaryRecords]);

  const handleChartViewToggle = (
    event: React.MouseEvent<HTMLElement>,
    newView: "customers" | "samples" | null
  ) => {
    if (newView) setChartView(newView);
  };

  const renderChart = () => {
    const selectedDataKey =
      chartView === "customers"
        ? "count_customers"
        : chartView === "samples"
        ? "count_samples"
        : "count_customers";

    const dataset = yoyChecked
      ? yoyChartData[selectedDataKey]?.data
      : chartData[selectedDataKey];

    if (loading) {
      return <CircularProgress />;
    }

    if (!dataset || dataset.length === 0) {
      return <Typography>No data available for this selection</Typography>;
    }

    const xAxisKey = yoyChecked ? "completed_date_period" : "x";
    const yAxisKey = "y";

    return (
      <BarChart
        dataset={dataset}
        xAxis={[
          {
            scaleType: "band",
            dataKey: xAxisKey,
            tickPlacement: "middle",
          },
        ]}
        series={yoyChecked ? seriesData : [{ dataKey: yAxisKey }]}
        borderRadius={9}
        height={500}
      />
    );
  };

  const columns_def: GridColDef[] = [
    {
      field: "id",
      headerName: granularity.value,
      width: 150,
      renderCell: (params: GridRenderCellParams) =>
        params.row.completed_date_period,
    },
    { field: "count_customers", headerName: "Total Evaluations", width: 150 },
    {
      field: "count_samples",
      headerName: "Total Samples",
      width: 150,
      renderCell: (params: GridRenderCellParams<Number>) =>
        formatNumber(params.value),
    },
    {
      field: "mean_sales_to_customer",
      headerName: "Average Sales to Customer",
      width: 150,
      renderCell: (params: GridRenderCellParams<Number>) =>
        formatNumber(params.value),
    },
    {
      field: "mean_ops_to_customer",
      headerName: "Average Ops to Customer",
      width: 150,
      renderCell: (params: GridRenderCellParams<Number>) =>
        formatNumber(params.value),
    },
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ flexGrow: 1 }}></Box>
          {/* Granularity Dropdown */}
          <FormControl>
            <InputLabel id="select-granularity-label">Granularity</InputLabel>
            <Select
              labelId="select-granularity-label"
              label="Granularity"
              value={granularity.label}
              onChange={(e) => {
                const selectedOption = granularityOptions.find(
                  (option) => option.label === e.target.value
                );
                if (selectedOption) {
                  setGranularity(selectedOption);
                }
              }}
              style={{ width: "200px" }}
            >
              {granularityOptions.map((option) => (
                <MenuItem key={option.value} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Grid>

      {/* Chart Section */}
      <Grid item xs={12}>
        <Card className="p-0" style={{ padding: "20px" }}>
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <ToggleButtonGroup
              color="primary"
              value={chartView}
              exclusive
              onChange={handleChartViewToggle}
              aria-label="chart-view"
            >
              <ToggleButton
                value="customers"
                aria-label="customers"
                style={{ width: "200px" }}
              >
                <Typography variant="button">Evaluations</Typography>
              </ToggleButton>
              <ToggleButton
                value="samples"
                aria-label="samples"
                style={{ width: "200px" }}
              >
                <Typography variant="button">Samples</Typography>
              </ToggleButton>
            </ToggleButtonGroup>

            <FormControlLabel
              control={
                <Switch
                  checked={yoyChecked}
                  onChange={() => setYoYToggle(!yoyChecked)}
                  color="primary"
                />
              }
              label="Year over year"
              style={{ marginLeft: "auto" }}
            />
          </Box>

          {renderChart()}
        </Card>
      </Grid>

      {/* Data Grid Section */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <GaneshDataGrid
              loading={loading}
              data={sampleSummaryRecords}
              columns={columns_def}
              gridId="historicallyCompletedSampleStatsGrid"
              defaultGridState={defaultGridState}
              customHeight="550px"
              showGridSaver={false}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default CompletedHistoricallySamples;
